import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip, TextField, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { StudioAddressBarcodeQuery } from '../../../services/query/studioService/StudioAddressBarcodeQuery';
import { StudioProductStatusQuery } from '../../../services/query/studioService/StudioProductStatusQuery';
import { StudioAddressBarcode, ProductStatusOption } from '../../../types/StudioAddressBarcode';
import { StudioAddressBarcodeDrawer } from './components/StudioAddressBarcodeDrawer';
import Swal from 'sweetalert2';
import './StudioAddressBarcodeManagement.scss';

export const StudioAddressBarcodeManagement = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<StudioAddressBarcode[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const searchBarcodeRef = useRef<HTMLInputElement>(null);
    const isActiveRef = useRef<HTMLInputElement>(null);
    const [filterKey, setFilterKey] = useState(0);

    // Actual filters used for fetching data
    const [appliedFilters, setAppliedFilters] = useState({
        barcode: '',
        isActive: 'all',
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedAddressBarcode, setSelectedAddressBarcode] = useState<StudioAddressBarcode | null>(null);
    const [addressBarcodeStatuses, setAddressBarcodeStatuses] = useState<ProductStatusOption[]>([]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        fetchStatuses();
        fetchData();
    }, [currentPage, pageSize, appliedFilters]);

    const fetchStatuses = async () => {
        try {
            const response = await StudioProductStatusQuery.GetStudioProductStatuses();
            let items = [];
            if (response?.model) {
                items = response.model;
            } else if (response?.data) {
                items = response.data;
            } else if (Array.isArray(response)) {
                items = response;
            } else if (response?.items) {
                items = response.items;
            }
            setAddressBarcodeStatuses(items || []);
        } catch (error) {
            console.error('Error fetching statuses:', error);
        }
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const params: any = {
                PageNumber: currentPage + 1,
                PageSize: pageSize,
            };

            if (appliedFilters.barcode) params.AddressBarcode = appliedFilters.barcode;
            if (appliedFilters.isActive === 'active') params.IsActive = true;
            if (appliedFilters.isActive === 'inactive') params.IsActive = false;

            const response = await StudioAddressBarcodeQuery.GetStudioAddressBarcodes(params);

            if (response) {
                // PagedResult<T> yapısı: Items, TotalCount
                const items = response.items || response.Items || [];
                const total = response.totalCount || response.TotalCount || 0;

                setData(items);
                setTotalCount(total);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studioAddressBarcode.fetchError'),
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
        // Apply current input values to filters which triggers useEffect
        setAppliedFilters({
            barcode: searchBarcodeRef.current?.value || '',
            isActive: isActiveRef.current?.value || 'all',
        });
    };

    const handleAdd = async () => {
        await fetchStatuses();
        setSelectedAddressBarcode(null);
        setDrawerOpen(true);
    };

    const handleEdit = async (addressBarcode: StudioAddressBarcode) => {
        await fetchStatuses();
        setSelectedAddressBarcode(addressBarcode);
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedAddressBarcode(null);
    };

    const handleSaveSuccess = () => {
        handleDrawerClose();
        fetchData();
    };

    const breadcrumbList = [
        { label: t('general.home'), path: '/home' },
        { label: t('studio.title'), path: '/studio' },
        { label: t('studioAddressBarcode.title') },
    ];

    const getRowId = (row: any) => {
        return row.addressBarcodeId || row.id || row.addressBarcodeDefinitionId || row.Id || row.addressDefinition;
    };

    const columns = useMemo(
        () => [
            {
                field: 'addressBarcode',
                headerName: t('studioAddressBarcode.barcode'),
                width: 150,
            },
            {
                field: 'addressDefinition',
                headerName: t('studioAddressBarcode.address'),
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'shotStatusId',
                headerName: t('studioAddressBarcode.shotStatus'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => {
                    const status = addressBarcodeStatuses.find((s) => s.shotStatusId === params.value);
                    if (!status) return params.value;
                    if (status.description) return status.description;
                    if (status.shotStatusKey) return t(status.shotStatusKey);
                    return status.name;
                },
            },
            {
                field: 'active',
                headerName: t('studioAddressBarcode.isActive'),
                width: 100,
                renderCell: (params: any) => (
                    <span style={{ color: params.row.active ? 'green' : 'red' }}>
                        {params.row.active ? t('general.active') : t('general.passive')}
                    </span>
                ),
            },
            {
                field: 'createdBy',
                headerName: t('studioAddressBarcode.createdBy'),
                width: 150,
                renderCell: (params: any) => (params.value ? params.value : '-'),
            },
            {
                field: 'updatedBy',
                headerName: t('studioAddressBarcode.updatedBy'),
                width: 150,
                renderCell: (params: any) => (params.value ? params.value : '-'),
            },
            {
                field: 'actions',
                headerName: t('general.actions'),
                width: 120,
                sortable: false,
                renderCell: (params: any) => (
                    <Box>
                        <Tooltip title={t('general.edit')}>
                            <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [t, addressBarcodeStatuses]
    );

    return (
        <SCUIContent>
            <>
                {isLoading && <SCUILoadingLinear />}
                <SCUIBreadCrumb items={breadcrumbList} />

                <Box className="studio-address-barcode-container">
                    <Box className="filters-section">
                        <TextField
                            label={t('studioAddressBarcode.barcode')}
                            inputRef={searchBarcodeRef}
                            defaultValue=""
                            size="small"
                            style={{ marginRight: 10, width: 200 }}
                        />
                        <TextField
                            key={filterKey}
                            select
                            label={t('studioAddressBarcode.isActive')}
                            defaultValue="all"
                            inputRef={isActiveRef}
                            size="small"
                            style={{ marginRight: 10, width: 150 }}
                        >
                            <MenuItem value="all">{t('general.all')}</MenuItem>
                            <MenuItem value="active">{t('general.active')}</MenuItem>
                            <MenuItem value="inactive">{t('general.passive')}</MenuItem>
                        </TextField>
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            size="m"
                            onClick={handleSearch}
                            style={{ marginRight: 10 }}
                            sizeType={'fixed'}
                        />
                        <SCUIButton
                            text={t('general.clear')}
                            type="secondary"
                            size="m"
                            onClick={() => {
                                if (searchBarcodeRef.current) searchBarcodeRef.current.value = '';
                                setFilterKey((prev) => prev + 1);
                                setAppliedFilters({
                                    barcode: '',
                                    isActive: 'all',
                                });
                                setCurrentPage(0);
                            }}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="actions-section">
                        <SCUIButton
                            text={t('general.addNew')}
                            type="primary"
                            size="m"
                            onClick={handleAdd}
                            showStartIcon
                            startIcon={<AddIcon />}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="grid-section">
                        <SCUICustomDataGrid
                            rows={data}
                            columns={columns}
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                            page={currentPage}
                            onPageChange={setCurrentPage}
                            rowCount={totalCount}
                            getRowId={getRowId}
                            paginationModeChange
                            loading={isLoading}
                        />
                    </Box>
                </Box>

                <StudioAddressBarcodeDrawer
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    addressBarcode={selectedAddressBarcode}
                    onSaveSuccess={handleSaveSuccess}
                    addressBarcodeStatuses={addressBarcodeStatuses}
                />
            </>
        </SCUIContent>
    );
};
